/**
 * PendingFormsGate — blocks the app on every authenticated load until any
 * outstanding form signatures (PHI Form for Portal Users, BAAs, etc.) are
 * collected. Mounts inside MainLayout so it's only active post-login.
 *
 * Flow:
 *   • On mount, GET /api/forms/pending → list of form templates the user
 *     has not signed at the latest version.
 *   • If empty, render nothing (the app is unblocked).
 *   • If non-empty, render a fullscreen modal showing the first pending
 *     form's body, a typed-name signature input, and a Sign & Agree button.
 *   • POST /api/forms/{id}/sign with the typed name. Backend captures
 *     timestamp + IP + user-agent.
 *   • Pop the form off the stack and continue with the next one. When the
 *     stack is empty the modal closes and the app becomes interactive.
 *
 * Re-checks pending whenever the user navigates (so editing the form body
 * in Settings → Forms while logged in causes a re-sign on next route).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, CheckCircle2, FileSignature } from 'lucide-react';
import api from '../../services/api';
import { safeInnerHtml } from '../../utils/sanitizeHtml';

export default function PendingFormsGate() {
  const location = useLocation();
  const [pending, setPending] = useState([]); // forms still to sign
  const [idx, setIdx] = useState(0);
  const [name, setName] = useState('');
  const [acked, setAcked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = useCallback(() => {
    api.get('/api/forms/pending')
       .then(r => setPending(r.data || []))
       .catch(() => setPending([]));
  }, []);

  useEffect(() => { refresh(); }, [refresh, location.pathname]);

  const current = pending[idx];

  // Reset per-form input when we advance to the next form
  useEffect(() => { setName(''); setAcked(false); setErr(null); }, [current?.id]);

  if (!current) return null;

  const submit = async () => {
    if (!name.trim()) { setErr('Please type your full name to sign.'); return; }
    if (!acked) { setErr('Please acknowledge that you have read and agree.'); return; }
    setSubmitting(true); setErr(null);
    try {
      await api.post(`/api/forms/${current.id}/sign`, { signed_name: name.trim() });
      // Pop this form off and advance
      const next = pending.filter((_, i) => i !== idx);
      setIdx(0);
      setPending(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to record signature.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileSignature size={20} color="#6366f1" />
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary, #111827)' }}>
                {current.name}
              </div>
              {pending.length > 1 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                  Form {idx + 1} of {pending.length} pending
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#dc2626', background: 'rgba(220,38,38,0.1)', padding: '4px 10px', borderRadius: 6 }}>
            SIGNATURE REQUIRED
          </div>
        </div>

        {/* Form body — admin-controlled HTML. Sanitized at source via
            bleach.clean() AND on render via safeInnerHtml — defense in
            depth so a future write-path bypass cannot lead to XSS. */}
        <div style={body}>
          <div
            style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary, #1f2937)' }}
            dangerouslySetInnerHTML={safeInnerHtml(current.body || '')}
          />
        </div>

        <div style={footer}>
          <label style={chk}>
            <input type="checkbox" checked={acked} onChange={e => setAcked(e.target.checked)}
                   style={{ width: 18, height: 18, accentColor: '#6366f1' }} />
            <span>I have read and agree to the terms above.</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Type your full legal name to sign"
              style={inp}
            />
            <button onClick={submit} disabled={submitting || !name.trim() || !acked}
                    style={{ ...btn, opacity: (submitting || !name.trim() || !acked) ? 0.5 : 1 }}>
              {submitting ? <><Loader2 size={14} className="spin" style={{ animation: 'spin 1s linear infinite' }} /> Signing…</> :
                            <><CheckCircle2 size={14} /> Sign &amp; Agree</>}
            </button>
          </div>
          {err && <div style={errStyle}>{err}</div>}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
            By clicking <em>Sign &amp; Agree</em> you create a legally-binding electronic signature. Your name, the timestamp, your IP address, and your browser are recorded for audit purposes.
          </div>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, zIndex: 9999,
  background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16,
};
const modal = {
  background: 'var(--card-bg, #fff)', color: 'var(--text-primary, #111827)',
  borderRadius: 14, boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
  width: '90vw', maxWidth: 820, maxHeight: '92vh',
  display: 'flex', flexDirection: 'column',
  border: '1px solid var(--border-color, #e5e7eb)',
};
const header = {
  padding: '18px 24px', borderBottom: '1px solid var(--border-color, rgba(0,0,0,0.08))',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const body = {
  padding: '20px 28px', flex: 1, overflowY: 'auto',
  background: 'var(--surface, rgba(0,0,0,0.02))',
};
const footer = {
  padding: '18px 24px', borderTop: '1px solid var(--border-color, rgba(0,0,0,0.08))',
  display: 'flex', flexDirection: 'column', gap: 10,
};
const chk = { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' };
const inp = { flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', fontSize: 14, outline: 'none', background: 'var(--input-bg, #fff)', color: 'var(--text-primary)' };
const btn = { padding: '10px 22px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' };
const errStyle = { padding: '8px 12px', background: '#FEF2F2', color: '#DC2626', borderRadius: 6, fontSize: 12 };
